import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { AuthService } from '../../services/auth/auth.service';
import { SystemErrorDataService } from '../../components/sgkz002d/services/system-error.service';
import { HttpErrorResponse } from '@angular/common/http';
import { LoginUserIdService } from '../../services/login-user-id.service';

/**
 * 認証確認機能
 * コンポーネント読み込み前に動作
 * @export
 * @class AuthGuard
 * @implements {CanActivate}
 */
@Injectable({
  providedIn: 'root',
})
export class AuthGuard {

  /**
   * コンストラクタ
   * @param {AuthService} authService
   * @param {Router} router
   * @memberof BrowserBackGuard
   */
  constructor(
    private authService: AuthService,
    private systemErrorDataService: SystemErrorDataService,
    private router: Router,
    private loginUserIdService: LoginUserIdService) { }

  /**
   * ルートコンポーネント読込み前チェック
   * 
   * @param {ActivatedRouteSnapshot} next
   * @param {RouterStateSnapshot} state
   * @return {boolean} 
   */
  async canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    return await this.checkUrl(state);
  }

  /**
   * ルート配下コンポーネント読込み前チェック
   * 
   * @param {ActivatedRouteSnapshot} next
   * @param {RouterStateSnapshot} state
   * @return {boolean} 
   */
  async canActivateChild(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    return await this.canActivate(next, state);
  }

  /**
   * 認証チェック結果
   *
   * @param {RouterStateSnapshot} state
   * @return {boolean} 
   */
  private async checkUrl(state: RouterStateSnapshot): Promise<boolean> {
    // 遷移先URL取得
    const nextUrl = state.url;

    // ログインチェック
    if (!this.authService.isAuthenticated()) {
      // 遷移先URL保存
      sessionStorage.setItem('returnUrl', nextUrl);
      // ログイン画面へ遷移
      this.router.navigate(['/login']);
      return false;
    }

    // マトリクスパラメータにユーザIDがあれば処理
    const paramString = nextUrl.split(';').slice(1).join('&');
    const queryParams = new URLSearchParams(paramString);

    // 外部システムの値によって'userId'変更
    const userId = queryParams.get('userId');
    if (userId !== null) {
      this.loginUserIdService.loginUserId = userId;
    }

    // 画面ID取得
    let semicolonNumber: number = nextUrl.indexOf(';');
    let semicolonSplitUrl: string = semicolonNumber > 0 ? nextUrl.slice(0, semicolonNumber) : nextUrl;
    let slashSplitUrl: string[] = semicolonSplitUrl.split('/');
    let screenId = slashSplitUrl[slashSplitUrl.length - 1];

    const targetScreenIdList: string[] = ['sgkz002d', 'sgkz003d', 'sgkz006d'];

    try {
      if (targetScreenIdList.includes(screenId) || await this.authService.isPageRolesAllowed(screenId.toUpperCase())) {
        return true;
      }
    } catch (error) {

      if (error instanceof HttpErrorResponse && error.status >= 500 && 599 >= error.status) {
        this.router.navigate(['/sgkz002d']);

      // kcis-authが組織兼務の場合、401エラーを返却するため
      } else if (error instanceof HttpErrorResponse && error.status === 401) {
        this.router.navigate(['/sgkz007d']);

      } else {
        this.moveSystemErrorScreen(error.message, '');
        this.router.navigate(['/sgkz002d']);
      }
      return;
    }

    this.moveSystemErrorScreen('', '画面表示権限がありません。');
    return;
  }

  /**
   * システムエラー画面遷移メソッド
   * 
   * ページ権限許可判定（AuthService）にて業務エラー発生時
   * または、ユーザーに対する画面表示権限が無い場合
   * 後続処理不可のためシステムエラー画面に遷移
   * 
   * @param {string} message
   * @param {string} errorDetail
   */
  private async moveSystemErrorScreen(message: string, errorDetail: string): Promise<void> {

    this.systemErrorDataService.systemErrorData = {
      messageId: '',
      message: message,
      errorDetail: errorDetail,
    }

    this.router.navigate(['/sgkz002d']);
  }
}
